import type { PresentChannelConfig, SurfApiConfig } from "@vida/surf-core";
import {
  ensureConfig,
  getAppsyncEndpoint,
  getAppsyncRealtimeEndpoint,
  getBedrockProxyUrlFromRuntime,
  getClientConfigUrl,
  getPdfPresignedUrlFromRuntime,
  getRagSearchUrlFromRuntime,
} from "../config";
import { getIdToken } from "../auth";
import { getAdminApiBaseUrl, setAdminApiBaseUrl } from "./surfAdminApi";
import {
  applyOceanBootstrap,
  setOceanAwsRegion,
  setOceanIdentityPoolId,
  setOceanStreamUrl,
  setOceanUserPoolId,
  setSurfResearcherAgentId,
  setSurfWhaleAgentId,
} from "./surfOceanConfig";

type ClientConfigPayload = {
  bedrock_proxy_url?: string;
  pdf_presigned_url?: string;
  rag_search_url?: string;
  admin_api_url?: string;
  ocean_agent_stream_url?: string;
  surf_researcher_agent_id?: string;
  surf_whale_agent_id?: string;
  cognito_identity_pool_id?: string;
  cognito_user_pool_id?: string;
  aws_region?: string;
};

const store = {
  bedrockProxyUrl: "",
  pdfPresignedUrl: "",
  ragSearchUrl: "",
  loaded: false,
};

function viteEnv(key: string): string {
  const raw = import.meta.env as Record<string, string | undefined>;
  return typeof raw[key] === "string" ? raw[key]!.trim() : "";
}

function applyBootstrapFromRuntime(): void {
  const bedrock = getBedrockProxyUrlFromRuntime();
  if (bedrock) store.bedrockProxyUrl = bedrock;
  const pdf = getPdfPresignedUrlFromRuntime();
  if (pdf) store.pdfPresignedUrl = pdf;
  const rag = getRagSearchUrlFromRuntime();
  if (rag) store.ragSearchUrl = rag;
}

export async function ensureSurfClientConfig(): Promise<void> {
  if (store.loaded) return;

  await ensureConfig();
  applyBootstrapFromRuntime();
  applyOceanBootstrap();

  const url = getClientConfigUrl() || viteEnv("VITE_CLIENT_CONFIG_URL");
  if (url) {
    try {
      const token = await getIdToken();
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Vida-AppId": "surf",
        },
      });
      if (res.ok) {
        const body = (await res.json()) as ClientConfigPayload;
        if (body.bedrock_proxy_url?.trim()) store.bedrockProxyUrl = body.bedrock_proxy_url.trim();
        if (body.pdf_presigned_url?.trim()) store.pdfPresignedUrl = body.pdf_presigned_url.trim();
        if (body.rag_search_url?.trim()) store.ragSearchUrl = body.rag_search_url.trim();
        if (body.admin_api_url?.trim()) setAdminApiBaseUrl(body.admin_api_url.trim());
        applyOceanFromClientPayload(body);
      }
    } catch {
      /* keep bootstrap from vida-cloud-surf-config.json */
    }
  }

  if (!store.bedrockProxyUrl) store.bedrockProxyUrl = viteEnv("VITE_BEDROCK_PROXY_URL");
  if (!store.pdfPresignedUrl) store.pdfPresignedUrl = viteEnv("VITE_PDF_PRESIGNED_URL");
  if (!store.ragSearchUrl) store.ragSearchUrl = viteEnv("VITE_RAG_SEARCH_URL");
  if (!getAdminApiBaseUrl()) {
    const admin = viteEnv("VITE_ADMIN_API_URL");
    if (admin) setAdminApiBaseUrl(admin);
  }

  store.loaded = true;
}

export function getBedrockProxyUrl(): string {
  return store.bedrockProxyUrl.replace(/\/+$/, "");
}

/** Dev: same-origin `/web/*` via vite proxy quando c'è un target bedrock. */
export function webProxyBaseForFetch(): string {
  if (import.meta.env.DEV && viteEnv("VITE_BEDROCK_PROXY_URL")) {
    return "";
  }
  return getBedrockProxyUrl();
}

/** Dev: same-origin `/bedrock/*` via vite proxy. */
export function bedrockProxyBaseForFetch(): string {
  return webProxyBaseForFetch();
}

export function requireWebProxyConfigured(): void {
  if (import.meta.env.DEV && viteEnv("VITE_BEDROCK_PROXY_URL")) return;
  if (getBedrockProxyUrl()) return;
  throw new Error(
    "Proxy Bedrock non configurato. In produzione: riesegui terraform apply (vida-cloud-surf-config.json) " +
      "e bash cloud/scripts/deploy-surf-spa.sh. In locale: bash cloud/scripts/surf-web-sync-env.sh.",
  );
}

export function getPdfPresignedUrl(): string {
  return store.pdfPresignedUrl.replace(/\/+$/, "");
}

export function getRagSearchUrl(): string {
  return store.ragSearchUrl.replace(/\/+$/, "");
}

export function createSurfApiConfig(): SurfApiConfig {
  return {
    endpoint: getAppsyncEndpoint(),
    getIdToken,
    // In dev con proxy Vite può essere "" (same-origin /bedrock/converse) — non usare || su URL pieno.
    bedrockProxyUrl: bedrockProxyBaseForFetch(),
    presignedBaseUrl: getPdfPresignedUrl() || undefined,
  };
}

export function createPresentChannelConfig(
  onError?: PresentChannelConfig["onError"],
): PresentChannelConfig {
  return {
    endpoint: getAppsyncEndpoint(),
    realtimeEndpoint: getAppsyncRealtimeEndpoint() || undefined,
    getIdToken,
    onError:
      onError ??
      ((err, phase) => {
        console.warn(`[present] ${phase} error`, err);
      }),
  };
}

export function createWebProxyConfig() {
  requireWebProxyConfigured();
  return {
    baseUrl: webProxyBaseForFetch(),
    getIdToken,
  };
}

export function createPdfSourceConfig() {
  return {
    presignedBaseUrl: getPdfPresignedUrl(),
    ragBaseUrl: ragApiBaseForFetch(),
    getIdToken,
  };
}

/** Dev: same-origin /rag/* via vite proxy. */
export function ragApiBaseForFetch(): string {
  if (import.meta.env.DEV && viteEnv("VITE_RAG_SEARCH_URL")) {
    return "";
  }
  return getRagSearchUrl();
}

export function pdfPresignedBaseForFetch(): string {
  return getPdfPresignedUrl();
}

function applyOceanFromClientPayload(body: ClientConfigPayload): void {
  if (body.ocean_agent_stream_url?.trim()) {
    setOceanStreamUrl(body.ocean_agent_stream_url.trim());
  }
  if (body.surf_researcher_agent_id?.trim()) {
    setSurfResearcherAgentId(body.surf_researcher_agent_id.trim());
  }
  if (body.surf_whale_agent_id?.trim()) {
    setSurfWhaleAgentId(body.surf_whale_agent_id.trim());
  }
  if (body.cognito_identity_pool_id?.trim()) {
    setOceanIdentityPoolId(body.cognito_identity_pool_id.trim());
  }
  if (body.cognito_user_pool_id?.trim()) {
    setOceanUserPoolId(body.cognito_user_pool_id.trim());
  }
  if (body.aws_region?.trim()) setOceanAwsRegion(body.aws_region.trim());
}

/** Re-load Oceany agent config (Whale/Researcher stream + Cognito for SigV4). Safe to call after login. */
export async function refreshSurfOceanClientConfig(): Promise<void> {
  applyOceanBootstrap();
  const url = getClientConfigUrl() || viteEnv("VITE_CLIENT_CONFIG_URL");
  if (!url) return;
  try {
    const token = await getIdToken();
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Vida-AppId": "surf",
      },
    });
    if (res.ok) {
      applyOceanFromClientPayload((await res.json()) as ClientConfigPayload);
    }
  } catch {
    /* keep bootstrap from .env / runtime */
  }
}
